import type { ReactNode } from "react";
import { Panel } from "@akaike/ui";

// Renders one platform-reported JSON blob (eta_map / extra / geofence) as readable
// structure instead of raw JSON. Shared by the Store page and the SKU x store page
// so both surface the same platform detail. Renders nothing when empty, so a grid
// of these only shows what the platform actually reported; unparseable strings fall
// back to a <pre>.
// Body-only variant: the same rendering without a Panel wrapper, for pages that fold
// these blobs into an existing panel as a sub-section (panels must never nest).
export function DataBody({ raw }: { raw: string }) {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return <pre className="mono jv-pre">{raw}</pre>;
  }
  // No height cap here: callers stack several of these inside one sub-section and frame
  // the group, so capping each blob would nest scrollers.
  return <div>{renderValue(parsed)}</div>;
}

export function DataPanel({ title, raw }: { title: string; raw: string }) {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return (
      <Panel>
        <Panel.Head title={title} />
        <Panel.Body tight>
          <pre className="mono jv-pre">{raw}</pre>
        </Panel.Body>
      </Panel>
    );
  }
  return (
    <Panel>
      <Panel.Head title={title} />
      <Panel.Body tight style={{ maxHeight: 320, overflow: "auto" }}>
        {renderValue(parsed)}
      </Panel.Body>
    </Panel>
  );
}

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

// human-readable key: snake/camel -> "Sentence case" words
function humanize(key: string): string {
  const spaced = key.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").trim();
  return spaced ? spaced[0].toUpperCase() + spaced.slice(1) : key;
}

function fmtScalar(v: unknown): string {
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return v.toLocaleString();
  return String(v);
}

// A value renders inline (2-column row) vs as an indented nested block.
function isInline(v: unknown): boolean {
  if (v === null || v === undefined || v === "") return true;
  if (Array.isArray(v)) return v.length === 0 || (v.every((x) => !isObj(x) && !Array.isArray(x)) && v.length <= 16);
  return !isObj(v);
}

// Uniform dict-of-objects (e.g. eta_map: {order_type: {eta, asset_id}}) -> render
// as a table. Returns the union of inner keys as columns, or null if not that shape.
function dictTableColumns(obj: Record<string, unknown>): string[] | null {
  const vals = Object.values(obj);
  if (vals.length < 2 || !vals.every(isObj)) return null;
  const cols: string[] = [];
  for (const v of vals) for (const k of Object.keys(v as object)) if (!cols.includes(k)) cols.push(k);
  return cols.length ? cols : null;
}

export function renderValue(v: unknown): ReactNode {
  if (v === null || v === undefined || v === "") return <span className="faint">—</span>;
  if (Array.isArray(v)) {
    if (v.length === 0) return <span className="faint">none</span>;
    if (v.every((x) => !isObj(x) && !Array.isArray(x))) {
      if (v.length <= 16) return <span>{v.map(fmtScalar).join(", ")}</span>;
      return <Collapsed value={v} label={`${v.length} values`} />;
    }
    if (v.length > 16) return <Collapsed value={v} label={`${v.length} items`} />;
    return (
      <div className="jv-arr">
        {v.map((x, i) => (
          <div className="jv-arr-item" key={i}>{renderValue(x)}</div>
        ))}
      </div>
    );
  }
  if (isObj(v)) {
    const cols = dictTableColumns(v);
    if (cols) {
      return (
        <table className="tbl jv">
          <thead>
            <tr>
              <th />
              {cols.map((c) => <th key={c} className="r">{humanize(c)}</th>)}
            </tr>
          </thead>
          <tbody>
            {Object.entries(v).map(([k, row]) => (
              <tr key={k}>
                <td className="strong" style={{ fontSize: 12 }}>{humanize(k)}</td>
                {cols.map((c) => (
                  <td key={c} className="r num">{renderValue((row as Record<string, unknown>)[c])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    return (
      <div className="jv-tree">
        {Object.entries(v).map(([k, val]) => {
          const inline = isInline(val);
          return (
            <div className={`jv-node ${inline ? "" : "nested"}`} key={k}>
              <div className="jv-k">{humanize(k)}</div>
              <div className="jv-v">{renderValue(val)}</div>
            </div>
          );
        })}
      </div>
    );
  }
  return <span>{fmtScalar(v)}</span>;
}

// Big arrays (e.g. geofence coordinate rings) collapse behind a summary so they
// don't blow out the panel -- expand to see the raw JSON.
function Collapsed({ value, label }: { value: unknown; label: string }) {
  return (
    <details className="jv-details">
      <summary className="faint">{label}</summary>
      <pre className="mono jv-pre">{JSON.stringify(value, null, 2)}</pre>
    </details>
  );
}
